import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { 
  ClipboardList, 
  Plus, 
  Save, 
  Loader2, 
  AlertTriangle,
  CheckCircle,
  Sparkles,
  Trash2,
  Camera,
  Image as ImageIcon,
  X,
  Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Condition ratings
const CONDITION_RATINGS = [
  { value: '1', label: '1 - No repair needed', color: 'bg-green-500' },
  { value: '2', label: '2 - Minor maintenance', color: 'bg-lime-500' },
  { value: '3', label: '3 - Repair/replacement needed', color: 'bg-yellow-500' },
  { value: '4', label: '4 - Urgent repair required', color: 'bg-red-500' }
];

// Urgency options
const URGENCY_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: '1_year', label: 'Within 1 Year' },
  { value: '3_year', label: 'Within 3 Years' },
  { value: '5_year', label: 'Within 5 Years' },
  { value: 'monitoring', label: 'Monitoring Only' }
];

export default function InspectionSheetDialog({ open, onClose, inspection, onSuccess }) {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [inspectionSheet, setInspectionSheet] = useState(null);
  const [findings, setFindings] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [newFinding, setNewFinding] = useState({
    section_id: '',
    element_id: '',
    element_name: '',
    condition_rating: 3,
    defect_description: '',
    urgency: 'monitoring',
    estimated_cost: 0,
    notes: '',
    photos: []
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && inspection) {
      fetchTemplates();
      checkExistingSheet();
    }
  }, [open, inspection]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/inspection-sheets/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
      if (response.data.length > 0) {
        setSelectedTemplate(response.data[0]);
        if (response.data[0].sections?.length > 0) {
          setActiveSection(response.data[0].sections[0].id);
        }
      }
    } catch (error) {
      // Template may not exist yet, try to seed it
      try {
        await axios.post(`${API}/inspection-sheets/templates/seed`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Retry fetch
        const retryResponse = await axios.get(`${API}/inspection-sheets/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(retryResponse.data);
        if (retryResponse.data.length > 0) {
          setSelectedTemplate(retryResponse.data[0]);
          if (retryResponse.data[0].sections?.length > 0) {
            setActiveSection(retryResponse.data[0].sections[0].id);
          }
        }
      } catch (seedError) {
        toast.error('Failed to load inspection templates');
      }
    }
  };

  const checkExistingSheet = async () => {
    try {
      const response = await axios.get(`${API}/inspection-sheets/inspection/${inspection.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.length > 0) {
        setInspectionSheet(response.data[0]);
        fetchFindings(response.data[0].id);
      }
    } catch (error) {
      // No existing sheet
    }
  };

  const fetchFindings = async (sheetId) => {
    try {
      const response = await axios.get(`${API}/inspection-sheets/${sheetId}/findings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFindings(response.data);
    } catch (error) {
      console.error('Failed to fetch findings:', error);
    }
  };

  const createInspectionSheet = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/inspection-sheets`, {
        inspection_id: inspection.id,
        template_id: selectedTemplate.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspectionSheet(response.data);
      toast.success('Inspection sheet created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create inspection sheet');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhoto(true);
    const uploadedPhotos = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/uploads/photo`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        uploadedPhotos.push(response.data.url);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (uploadedPhotos.length > 0) {
      setNewFinding(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedPhotos]
      }));
      toast.success(`${uploadedPhotos.length} photo(s) uploaded`);
    }

    setUploadingPhoto(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setNewFinding(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const addFinding = async () => {
    if (!inspectionSheet) return;
    if (!newFinding.element_name) {
      toast.error('Please select an element');
      return;
    }
    if (!newFinding.defect_description) {
      toast.error('Please describe the defect');
      return;
    }

    setSaving(true);
    try {
      const findingData = {
        ...newFinding,
        section_id: activeSection || newFinding.section_id,
        element_id: newFinding.element_name.toLowerCase().replace(/\s+/g, '_')
      };

      const response = await axios.post(`${API}/inspection-sheets/${inspectionSheet.id}/findings`, findingData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFindings([...findings, response.data]);
      
      // Reset form
      setNewFinding({
        section_id: activeSection || '',
        element_id: '',
        element_name: '',
        condition_rating: 3,
        defect_description: '',
        urgency: 'monitoring',
        estimated_cost: 0,
        notes: '',
        photos: []
      });
      toast.success('Finding added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add finding');
    } finally {
      setSaving(false);
    }
  };

  const analyzeWithAI = async () => {
    if (!newFinding.element_name || !newFinding.defect_description) {
      toast.error('Please enter element name and defect description');
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/inspection-sheets/ai/analyze-defect`, {
        element_name: newFinding.element_name,
        description: newFinding.defect_description,
        condition_rating: newFinding.condition_rating
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const suggestion = response.data;
      setNewFinding(prev => ({
        ...prev,
        urgency: suggestion.suggested_urgency || prev.urgency,
        estimated_cost: suggestion.suggested_cost_range?.max || prev.estimated_cost,
        notes: `AI Suggestion: ${suggestion.reasoning}\n\nRecommendations:\n${suggestion.recommendations?.join('\n') || ''}`
      }));
      toast.success('AI analysis complete');
    } catch (error) {
      toast.error('AI analysis unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  const calculateTotalCost = () => {
    return findings.reduce((sum, f) => sum + (f.estimated_cost || 0), 0);
  };

  const getRatingColor = (rating) => {
    const found = CONDITION_RATINGS.find(r => r.value === String(rating));
    return found?.color || 'bg-gray-500';
  };

  const getPhotoUrl = (photo) => {
    if (photo.startsWith('http')) return photo;
    return `${BACKEND_URL}${photo}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="inspection-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-accent" />
            Inspection Sheet
          </DialogTitle>
          <DialogDescription>
            {inspection?.property_address}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {!inspectionSheet ? (
            // Template Selection
            <div className="space-y-4 p-4">
              <Label>Select Inspection Template</Label>
              <Select
                value={selectedTemplate?.id || ''}
                onValueChange={(value) => {
                  const template = templates.find(t => t.id === value);
                  setSelectedTemplate(template);
                }}
              >
                <SelectTrigger data-testid="template-select">
                  <SelectValue placeholder="Choose template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedTemplate && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-lg">{selectedTemplate.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">{selectedTemplate.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.sections?.map((section) => (
                        <Badge key={section.id} variant="outline">
                          {section.name || section.title}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Button 
                onClick={createInspectionSheet} 
                disabled={loading || !selectedTemplate}
                className="w-full"
                data-testid="start-sheet-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Start Inspection Sheet
              </Button>
            </div>
          ) : (
            // Inspection Sheet Form
            <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
              <TabsList className="w-full flex-wrap h-auto gap-1 p-1">
                {selectedTemplate?.sections?.map((section) => (
                  <TabsTrigger 
                    key={section.id} 
                    value={section.id}
                    className="text-xs"
                    data-testid={`section-tab-${section.id}`}
                  >
                    {section.name || section.title}
                  </TabsTrigger>
                ))}
              </TabsList>

              {selectedTemplate?.sections?.map((section) => (
                <TabsContent key={section.id} value={section.id} className="space-y-4 mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">{section.name || section.title}</CardTitle>
                      <p className="text-sm text-muted-foreground">{section.description}</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Existing Findings for this section */}
                      {findings.filter(f => f.section_id === section.id).length > 0 && (
                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Recorded Findings</Label>
                          {findings.filter(f => f.section_id === section.id).map((finding, idx) => (
                            <div key={finding.id || idx} className="p-4 border rounded-lg bg-muted/30" data-testid={`finding-${finding.id}`}>
                              <div className="flex items-start gap-3">
                                <div className={`w-3 h-3 rounded-full mt-1 ${getRatingColor(finding.condition_rating)}`} />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium">{finding.element_name}</span>
                                    <Badge variant="outline" className="text-xs">
                                      Rating: {finding.condition_rating}
                                    </Badge>
                                    <Badge variant="secondary" className="text-xs">
                                      {URGENCY_OPTIONS.find(u => u.value === finding.urgency)?.label || finding.urgency}
                                    </Badge>
                                  </div>
                                  {finding.defect_description && (
                                    <p className="text-sm text-muted-foreground mt-2">{finding.defect_description}</p>
                                  )}
                                  {finding.notes && (
                                    <p className="text-xs text-muted-foreground mt-1 italic">{finding.notes}</p>
                                  )}
                                  {finding.estimated_cost > 0 && (
                                    <p className="text-sm text-accent font-medium mt-2">Est. Cost: £{finding.estimated_cost.toLocaleString()}</p>
                                  )}
                                  {/* Photos */}
                                  {finding.photos && finding.photos.length > 0 && (
                                    <div className="flex gap-2 mt-3 flex-wrap">
                                      {finding.photos.map((photo, photoIdx) => (
                                        <img
                                          key={photoIdx}
                                          src={getPhotoUrl(photo)}
                                          alt={`Finding photo ${photoIdx + 1}`}
                                          className="w-20 h-20 object-cover rounded-lg border"
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          <Separator className="my-4" />
                        </div>
                      )}

                      {/* Add New Finding Form */}
                      <div className="space-y-4 pt-2 border-t">
                        <div className="flex items-center gap-2 pt-4">
                          <Plus className="h-5 w-5 text-accent" />
                          <Label className="text-base font-semibold">Add New Finding</Label>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="element_name">Element Name *</Label>
                            <Select
                              value={newFinding.element_name}
                              onValueChange={(value) => setNewFinding({...newFinding, element_name: value, section_id: section.id})}
                            >
                              <SelectTrigger data-testid="element-select">
                                <SelectValue placeholder="Select element" />
                              </SelectTrigger>
                              <SelectContent>
                                {section.fields?.map((field) => (
                                  <SelectItem key={field.id} value={field.label}>
                                    {field.label}
                                  </SelectItem>
                                ))}
                                <SelectItem value="other">Other (specify in notes)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="condition_rating">Condition Rating *</Label>
                            <Select
                              value={String(newFinding.condition_rating)}
                              onValueChange={(value) => setNewFinding({...newFinding, condition_rating: parseInt(value)})}
                            >
                              <SelectTrigger data-testid="condition-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CONDITION_RATINGS.map((rating) => (
                                  <SelectItem key={rating.value} value={rating.value}>
                                    <div className="flex items-center gap-2">
                                      <div className={`w-3 h-3 rounded-full ${rating.color}`} />
                                      {rating.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="defect_description">Defect Description *</Label>
                          <Textarea
                            id="defect_description"
                            placeholder="Describe the defect or issue observed on-site..."
                            value={newFinding.defect_description}
                            onChange={(e) => setNewFinding({...newFinding, defect_description: e.target.value})}
                            rows={3}
                            data-testid="defect-description-input"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="urgency">Urgency</Label>
                            <Select
                              value={newFinding.urgency}
                              onValueChange={(value) => setNewFinding({...newFinding, urgency: value})}
                            >
                              <SelectTrigger data-testid="urgency-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {URGENCY_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="estimated_cost">Estimated Cost (£)</Label>
                            <Input
                              id="estimated_cost"
                              type="number"
                              value={newFinding.estimated_cost || ''}
                              onChange={(e) => setNewFinding({...newFinding, estimated_cost: parseFloat(e.target.value) || 0})}
                              placeholder="0"
                              data-testid="cost-input"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="notes">Additional Notes</Label>
                          <Textarea
                            id="notes"
                            placeholder="Additional notes about the finding..."
                            value={newFinding.notes}
                            onChange={(e) => setNewFinding({...newFinding, notes: e.target.value})}
                            rows={2}
                            data-testid="notes-input"
                          />
                        </div>

                        {/* Photo Upload Section */}
                        <div>
                          <Label className="mb-2 block">Photos</Label>
                          <div className="space-y-3">
                            {/* Photo Preview */}
                            {newFinding.photos.length > 0 && (
                              <div className="flex gap-2 flex-wrap">
                                {newFinding.photos.map((photo, idx) => (
                                  <div key={idx} className="relative group">
                                    <img
                                      src={getPhotoUrl(photo)}
                                      alt={`Upload ${idx + 1}`}
                                      className="w-24 h-24 object-cover rounded-lg border"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removePhoto(idx)}
                                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Upload Button */}
                            <div className="flex items-center gap-2">
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handlePhotoUpload}
                                className="hidden"
                                id="photo-upload"
                                data-testid="photo-upload-input"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingPhoto}
                                className="gap-2"
                                data-testid="upload-photo-btn"
                              >
                                {uploadingPhoto ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Camera className="h-4 w-4" />
                                )}
                                {uploadingPhoto ? 'Uploading...' : 'Add Photos'}
                              </Button>
                              <span className="text-xs text-muted-foreground">
                                Supports JPG, PNG, WebP (max 10MB)
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={analyzeWithAI}
                            disabled={aiLoading || !newFinding.element_name || !newFinding.defect_description}
                            data-testid="ai-analyze-btn"
                          >
                            {aiLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                            AI Suggest
                          </Button>
                          <Button
                            type="button"
                            onClick={addFinding}
                            disabled={saving || !newFinding.element_name || !newFinding.defect_description}
                            data-testid="add-finding-btn"
                          >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                            Add Finding
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>

        {inspectionSheet && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Total Findings</p>
                  <p className="font-bold text-lg">{findings.length}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Estimated Total Cost</p>
                  <p className="font-bold text-lg text-accent">£{calculateTotalCost().toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Risk Score</p>
                  <p className="font-bold text-lg">{inspectionSheet.risk_score?.toFixed(1) || '0.0'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} data-testid="close-sheet-btn">
                  Close
                </Button>
                <Button onClick={() => { onSuccess?.(); onClose(); }} data-testid="save-sheet-btn">
                  <Save className="h-4 w-4 mr-2" />
                  Save & Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {!inspectionSheet && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
